import { useCallback, useRef, useState } from 'react';

import { ApiError } from '@/lib/api/client';
import { apiUpload, isUploadCancelled } from '@/lib/api/uploads';
import type { UploadLimits, UploadResult } from '@/lib/api/types';

import { precheckFile } from './precheck';

export type UploadStatus = 'idle' | 'uploading' | 'error';

export interface UploadFailure {
  /** Customer-facing prose. The server's own words when the server spoke. */
  message: string;
  /** True when pressing "Try again" with the SAME file could work. */
  retryable: boolean;
  /** Support handle, shown small. Never the primary text. */
  requestId?: string | undefined;
}

export interface FileUploadState {
  status: UploadStatus;
  /** The file in flight, or the one that failed. Null when idle. */
  file: File | null;
  /** 0-100 while bytes are moving; undefined when the length is not computable. */
  percent: number | undefined;
  error: UploadFailure | null;
}

export interface UseFileUploadArgs {
  /** From GET /api/v1/meta. Undefined while it loads — prechecks then pass through. */
  limits: UploadLimits | undefined;
  /** Fired once the server has taken the file (201 report or 202 poll target). */
  onUploaded: (result: UploadResult, file: File) => void;
}

const IDLE: FileUploadState = { status: 'idle', file: null, percent: undefined, error: null };

/**
 * The upload state machine: pick → precheck → transfer → hand off.
 *
 * It owns exactly one piece of mutable state, and that is fine: an in-flight
 * upload is genuinely ephemeral. Reloading mid-transfer must lose it, because
 * the bytes are gone; what must NOT be lost is anything after the server
 * answered, and that is why `onUploaded` puts the id in the URL immediately.
 *
 * Cancel is a real abort of the socket, not a UI lie that hides a request still
 * burning the customer's data.
 */
export function useFileUpload({ limits, onUploaded }: UseFileUploadArgs) {
  const [state, setState] = useState<FileUploadState>(IDLE);
  const abortRef = useRef<AbortController | null>(null);
  // Guards against a late progress event from an aborted transfer resurrecting
  // the progress bar after the user pressed Cancel.
  const runIdRef = useRef(0);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    runIdRef.current += 1;
    setState(IDLE);
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    runIdRef.current += 1;
    setState(IDLE);
  }, []);

  const start = useCallback(
    async (file: File) => {
      const precheck = precheckFile(file, limits);
      if (!precheck.ok) {
        // A wrong file is not a retry: the same bytes fail the same way, so the
        // only sensible action is "choose another file".
        setState({
          status: 'error',
          file,
          percent: undefined,
          error: { message: precheck.message, retryable: false },
        });
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      runIdRef.current += 1;
      const runId = runIdRef.current;

      setState({ status: 'uploading', file, percent: 0, error: null });

      try {
        const result = await apiUpload({
          file,
          signal: controller.signal,
          onProgress: ({ percent }) => {
            if (runIdRef.current !== runId) return;
            setState((previous) =>
              previous.status === 'uploading' ? { ...previous, percent } : previous,
            );
          },
        });
        if (runIdRef.current !== runId) return;
        abortRef.current = null;
        setState(IDLE);
        onUploaded(result, file);
      } catch (thrown) {
        if (runIdRef.current !== runId) return;
        abortRef.current = null;
        // Cancelling is a choice, not a failure. Say nothing and go back to idle.
        if (isUploadCancelled(thrown)) {
          setState(IDLE);
          return;
        }
        const apiError = thrown instanceof ApiError ? thrown : null;
        setState({
          status: 'error',
          file,
          percent: undefined,
          error: {
            message:
              apiError?.message ?? 'We could not upload that file. Please try again in a moment.',
            // A 4xx about the file itself will fail identically on retry.
            retryable: apiError ? apiError.isRetryable || apiError.isOffline : true,
            requestId: apiError?.requestId,
          },
        });
      }
    },
    [limits, onUploaded],
  );

  const retry = useCallback(() => {
    const file = state.file;
    if (!file) return;
    void start(file);
  }, [start, state.file]);

  return { state, start, cancel, retry, reset } as const;
}
